import Section from "components/layout/section";
import SectionHeader from "components/layout/sectionHeader";
import SectionHeaderTitle from "components/layout/sectionHeaderTitle";
import { useTranslation } from "react-i18next";
import PageTitle from "features/navigation/pageTitle";
import RightSidebar from "@/components/sidebar/right/RightSidebar";
import Button from "@/components/inputs/button";
import useAuth from "@/hooks/useAuth";
import { ReactComponent as SignUpIcon } from "assets/icons/group_add.svg";
import { ReactComponent as AddIcon } from "assets/icons/add.svg";
import { ReactComponent as ListIcon } from "assets/icons/format_list_bulleted.svg";
import Hint from "@/components/text/Hint"
import LegalLinks from "@/components/legal/LegalLinks";
import { useHideMainSheet } from "@/components/layout/layoutChrome";
import { StoragePlan } from "types";

const PLANS = [
    {
        plan: StoragePlan.free,
        titleKey: "storagePlanFree",
        priceKey: "pricingPriceFree",
        descriptionKey: "storagePlanFreeDescription",
        featureKeys: [
            "storagePlanFreeProducts",
            "storagePlanFreeCategories",
            "storagePlanFreeCustomInputs",
        ],
    },
    {
        plan: StoragePlan.pro,
        titleKey: "storagePlanPro",
        priceKey: "pricingPricePaidPlaceholder",
        descriptionKey: "storagePlanProDescription",
        featureKeys: [
            "storagePlanProProducts",
            "storagePlanProCategories",
            "storagePlanProTransactions",
            "storagePlanProSharedAccess",
            "storagePlanProCustomers",
            "storagePlanProSuppliers",
            "storagePlanProCustomInputs",
            "storagePlanProAiSuggestions",
        ],
    },
    {
        plan: StoragePlan.premium,
        titleKey: "storagePlanPremium",
        priceKey: "pricingPricePaidPlaceholder",
        descriptionKey: "storagePlanPremiumDescription",
        featureKeys: [
            "storagePlanPremiumProducts",
            "storagePlanPremiumCategories",
            "storagePlanPremiumTransactions",
            "storagePlanPremiumSharedAccess",
            "storagePlanPremiumCustomers",
            "storagePlanPremiumSuppliers",
            "storagePlanPremiumCustomInputs",
            "storagePlanPremiumAiSuggestions",
        ],
    },
] as const;

const PricingPage = () => {
    const { t } = useTranslation();
    const { tokenIsValid } = useAuth();
    const isLoggedIn = tokenIsValid === true;
    useHideMainSheet();

    return (
        <>
            <PageTitle title={t("titlePricing")} />
            <Section design="panel">
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h1 className="whitespace-normal">{t("titlePricing")}</h1>
                    </SectionHeaderTitle>
                </SectionHeader>
                <p>{t("pricingIntro")}</p>
                <Hint>{t("pricingNote")}</Hint>
                <div className="flex flex-col gap-2 md:flex-row md:flex-wrap">
                    {isLoggedIn ? (
                        <Button wideOnMobile design="primary" to="/storages/create">
                            <AddIcon />
                            <span>{t("createStorage")}</span>
                        </Button>
                    ) : (
                        <Button wideOnMobile design="primary" to="/sign-up">
                            <SignUpIcon />
                            <span>{t("buttonSignUpForFree")}</span>
                        </Button>
                    )}
                    <Button wideOnMobile design="white" to="/faq">
                        <ListIcon />
                        <span>{t("buttonViewFaq")}</span>
                    </Button>
                </div>
            </Section>
            <Section design="card-grid-3">
                {PLANS.map(({ plan, titleKey, priceKey, descriptionKey, featureKeys }) => (
                    <Section key={plan} design="panel">
                        <h2>{t(titleKey)}</h2>
                        <p className="text-xl font-bold">{t(priceKey)}</p>
                        <p>{t(descriptionKey)}</p>
                        <ul className="list-disc pl-6 flex flex-col gap-y-1">
                            {featureKeys.map((key) => (
                                <li key={key}>{t(key)}</li>
                            ))}
                        </ul>
                    </Section>
                ))}
            </Section>
            <RightSidebar />
        </>
    );
};

export default PricingPage;
